/* ==========================================================================
   STF DIAMONDS — pdp.js
   Product page behaviors (vanilla, no CDNs):
   1. Gallery      — thumb rail <-> snap-scroll stage sync (mobile slider).
   2. Variants     — metal swatches / selects -> price, ATC button price,
                     stock line, gallery media, URL (?variant=).
   3. Add to cart  — AJAX add, opens the cart drawer when present,
                     falls back to the cart page.
   4. Sticky ATC   — bottom bar appears once the buy-box CTA scrolls away.
   5. Recs         — fetches "Complete the Look" / "You May Also Like"
                     via the Product Recommendations + Section Rendering
                     APIs; empty responses keep the section hidden.
   Loaded (defer) by stf-product.liquid and stf-recommendations.liquid;
   the guard below keeps duplicate tags harmless.
   ========================================================================== */
(function () {
  'use strict';

  if (window.STFPDPInit) return;
  window.STFPDPInit = true;

  var doc = document;

  /* ------------------------------------------------------------------
     Money formatting (uses the shop money format from [data-pdp])
     ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function addCommas(num) {
      return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    switch ((formatString.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100));
        break;
      case 'amount_with_comma_separator':
        value = addCommas(Math.floor(cents / 100)).replace(/,/g, '.') + ',' +
          ('0' + (Math.round(cents) % 100)).slice(-2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addCommas(Math.round(cents / 100)).replace(/,/g, '.');
        break;
      default:
        value = addCommas(Math.floor(cents / 100)) + '.' +
          ('0' + (Math.round(cents) % 100)).slice(-2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------
     PDP root
     ------------------------------------------------------------------ */
  function initPdp(root) {
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';
    var cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add';

    /* ---------------- 1. GALLERY ---------------- */
    var track = root.querySelector('[data-gallery-track]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function goToSlide(index) {
      if (!track || !slides[index]) return;
      track.scrollTo({
        left: slides[index].offsetLeft - track.offsetLeft,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    function setActiveThumb(index) {
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = parseInt(thumb.getAttribute('data-index'), 10) || 0;
        goToSlide(index);
        setActiveThumb(index);
      });
    });

    if (track && slides.length > 1) {
      var scrollTimer = null;
      track.addEventListener('scroll', function () {
        if (scrollTimer) window.cancelAnimationFrame(scrollTimer);
        scrollTimer = window.requestAnimationFrame(function () {
          var index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          setActiveThumb(Math.min(index, slides.length - 1));
        });
      }, { passive: true });
    }

    function showVariantMedia(mediaId) {
      if (!mediaId) return;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          goToSlide(i);
          setActiveThumb(i);
          return;
        }
      }
    }

    /* ---------------- 2. VARIANT PICKER ---------------- */
    var form = root.querySelector('[data-product-form]');
    var variantDataEl = root.querySelector('[data-variant-data]');
    var variants = [];
    if (variantDataEl) {
      try { variants = JSON.parse(variantDataEl.textContent); } catch (err) { variants = []; }
    }

    // Every variant-id input in the section: the main product form plus the
    // sticky-bar payment-terms form. Each gets synced on variant change and
    // receives a bubbling `change` event so <shopify-payment-terms>
    // recomputes the installment amount.
    var idInputs = Array.prototype.slice.call(
      root.querySelectorAll('input[data-variant-id]')
    );
    var priceWrappers = Array.prototype.slice.call(
      root.querySelectorAll('[data-price-wrapper], .sticky-atc__price')
    );
    var atcBtn = root.querySelector('[data-atc]');
    var atcCompact = root.querySelector('[data-atc-compact]');
    var stockLine = root.querySelector('[data-stock-line]');

    function selectedOptions() {
      var values = [];
      var groups = Array.prototype.slice.call(root.querySelectorAll('[data-option-position]'));
      groups.sort(function (a, b) {
        return parseInt(a.getAttribute('data-option-position'), 10) -
          parseInt(b.getAttribute('data-option-position'), 10);
      });
      groups.forEach(function (group) {
        var checked = group.querySelector('input[data-option-input]:checked');
        var select = group.querySelector('select[data-option-input]');
        if (checked) {
          values.push(checked.value);
        } else if (select) {
          values.push(select.value);
        }
      });
      return values;
    }

    function findVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (variants[i].options[j] !== options[j]) { match = false; break; }
        }
        if (match) return variants[i];
      }
      return null;
    }

    function renderPrice(wrapper, price, compareAt) {
      var priceEl = wrapper.querySelector('[data-price]');
      var compareEl = wrapper.querySelector('[data-compare-price]');
      var onSale = compareAt > price;
      if (priceEl) {
        priceEl.textContent = formatMoney(price, moneyFormat);
        priceEl.classList.toggle('price--sale', onSale);
      }
      if (compareEl) {
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? formatMoney(compareAt, moneyFormat) : '';
      }
    }

    function updateAtc(variant) {
      var addText = atcBtn ? atcBtn.getAttribute('data-text-add') : 'Add to Cart';
      var soldText = atcBtn ? atcBtn.getAttribute('data-text-sold-out') : 'Sold';
      var available = !!(variant && variant.available);

      [atcBtn, atcCompact].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !available;
      });

      if (atcBtn) {
        var label = atcBtn.querySelector('[data-atc-label]');
        var sep = atcBtn.querySelector('.pdp-atc__sep');
        var priceEl = atcBtn.querySelector('[data-atc-price]');
        if (label) label.textContent = available ? addText : soldText;
        if (sep) sep.hidden = !available;
        if (priceEl) {
          priceEl.hidden = !available;
          if (available) priceEl.textContent = formatMoney(variant.price, moneyFormat);
        }
      }

      if (atcCompact) {
        atcCompact.textContent = available ? addText : soldText;
      }
    }

    function updateStock(variant) {
      if (!stockLine) return;
      var textEl = stockLine.querySelector('[data-stock-text]');
      var available = !!(variant && variant.available);
      stockLine.classList.toggle('pdp-stock--out', !available);
      if (textEl) {
        textEl.textContent = available
          ? stockLine.getAttribute('data-text-in-stock')
          : stockLine.getAttribute('data-text-sold-out');
      }
    }

    function updateSwatchState(group) {
      var checked = group.querySelector('input[data-option-input]:checked');
      group.querySelectorAll('label.swatch').forEach(function (label) {
        var input = doc.getElementById(label.getAttribute('for'));
        label.classList.toggle('is-active', input === checked);
      });
      var valueEl = group.querySelector('[data-selected-value]');
      if (valueEl && checked) valueEl.textContent = checked.value;
    }

    function onOptionChange() {
      var variant = findVariant(selectedOptions());

      root.querySelectorAll('.pdp-option--swatch').forEach(updateSwatchState);

      if (!variant) {
        updateAtc(null);
        updateStock(null);
        return;
      }

      idInputs.forEach(function (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      priceWrappers.forEach(function (wrapper) {
        renderPrice(wrapper, variant.price, variant.compare_at_price || 0);
      });
      updateAtc(variant);
      updateStock(variant);
      showVariantMedia(variant.media_id);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.querySelectorAll('[data-option-input]').forEach(function (input) {
      input.addEventListener('change', onOptionChange);
    });

    /* ---------------- 3. ADD TO CART (AJAX) ---------------- */
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var errorEl = root.querySelector('[data-atc-error]');
        if (errorEl) errorEl.hidden = true;

        var submitBtns = [atcBtn, atcCompact].filter(Boolean);
        submitBtns.forEach(function (btn) { btn.classList.add('is-loading'); btn.disabled = true; });

        fetch(cartAddUrl + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (res) {
            return res.json().then(function (json) {
              if (!res.ok) throw json;
              return json;
            });
          })
          .then(function (item) {
            doc.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: { item: item } }));
            doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            // Update any header cart count bubble
            fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
              .then(function (res) { return res.json(); })
              .then(function (cart) {
                doc.querySelectorAll('[data-cart-count]').forEach(function (el) {
                  el.textContent = cart.item_count;
                  el.classList.toggle('is-empty', cart.item_count === 0);
                });
              })
              .catch(function () {});

            var drawer = doc.querySelector('#CartDrawer, #cart-drawer, [data-cart-drawer]');
            if (drawer && window.STF && window.STF.openOverlay) {
              window.STF.openOverlay(drawer);
            } else if (!drawer) {
              window.location.href = cartUrl;
            }
          })
          .catch(function (err) {
            if (errorEl) {
              errorEl.textContent = (err && (err.description || err.message)) ||
                'Something went wrong. Please try again.';
              errorEl.hidden = false;
            }
          })
          .finally(function () {
            submitBtns.forEach(function (btn) { btn.classList.remove('is-loading'); });
            // Restore disabled state from current variant availability
            var variant = variants.length ? findVariant(selectedOptions()) : null;
            var available = variants.length ? !!(variant && variant.available) : true;
            submitBtns.forEach(function (btn) { btn.disabled = !available; });
          });
      });
    }

    /* ---------------- 4. STICKY ATC BAR ---------------- */
    var stickyBar = root.querySelector('[data-sticky-atc]');
    var ctaRow = root.querySelector('[data-cta-row]');
    if (stickyBar && ctaRow && 'IntersectionObserver' in window) {
      var stickyObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', passed);
          stickyBar.setAttribute('aria-hidden', passed ? 'false' : 'true');
        });
      }, { threshold: 0 });
      stickyObserver.observe(ctaRow);
    }
  }

  doc.querySelectorAll('[data-pdp]').forEach(initPdp);

  /* ------------------------------------------------------------------
     5. RECOMMENDATION CAROUSELS — fetch + inject
     ------------------------------------------------------------------ */
  function loadRecommendations(container) {
    var url = container.getAttribute('data-recommendations-url');
    if (!url || container.getAttribute('data-loaded')) return;
    container.setAttribute('data-loaded', 'true');

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var temp = doc.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.rec-section__inner')) {
          container.innerHTML = fresh.innerHTML;
          container.classList.remove('rec-section--pending');
          // Re-run shared initializers (carousel buttons, lazy images)
          container.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
          if (window.STF && window.STF.watchImages) window.STF.watchImages(container);
        }
      })
      .catch(function () {});
  }

  doc.querySelectorAll('[data-recommendations]').forEach(loadRecommendations);
})();
